import React from 'react';
import { ExperimentId } from 'core/type/ExperimentId';
import { isAnyB } from 'core/window/bootstrapConfig';
import { checkCondition, ConditionType } from 'component/common/Experiment/Condition';
import { useSelector } from 'react-redux';
import { selectExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { Experiments } from 'core/type/BootstrapConfig.types';

export interface ExperimentProps {
    /**
     * ExperimentId values to check for.<br/>
     * <b>ExperimentVariant</b> component will consider the experiment to be <b>B</b> when at least ExperimentId is <b>true</b>
     */
    id: ExperimentId | ExperimentId[];

    /**
     * Condition to combine with Experiment check.<br/>
     * Condition check is executed before checking for experiment variant using 'isB'
     */
    when?: ConditionType;

    /**
     * Check experiment to have <b>'B'</b> variant.<br/>
     * Default: true <br/>
     * Set checkForBVariant={false} to check for <b>'A'</b> variant.
     */
    checkForBVariant?: boolean;
}

export type ExperimentVariantProps = Omit<ExperimentProps, 'checkForBVariant'>;

const isExperimentEnabled = (experiments: Experiments, expId: ExperimentProps['id'], checkForBVariant = true): boolean => {
    const experimentIds = Array.isArray(expId) ? expId : [expId];
    const experimentEnabled = isAnyB(experiments, experimentIds);
    return checkForBVariant ? experimentEnabled : !experimentEnabled;
};

const ExperimentVariant: React.FC<React.PropsWithChildren<ExperimentProps>> = React.memo((props) => {
    const experiments = useSelector(selectExperimentsFromBootstrap);

    const conditionSuccess = props.when === null || props.when === undefined ? true : checkCondition(props.when);
    if (!conditionSuccess) {
        return null;
    }

    const experimentEnabled = isExperimentEnabled(experiments, props.id, props.checkForBVariant);
    if (!experimentEnabled) {
        return null;
    }
    return <>{props.children}</>;
});
ExperimentVariant.displayName = 'ExperimentVariant';

const ExperimentVariantA: React.FC<React.PropsWithChildren<ExperimentVariantProps>> = (props) => {
    return (
        <ExperimentVariant id={props.id} when={props.when} checkForBVariant={false}>
            {props.children}
        </ExperimentVariant>
    );
};
ExperimentVariantA.displayName = 'ExperimentVariantA';

const ExperimentVariantB: React.FC<React.PropsWithChildren<ExperimentVariantProps>> = (props) => {
    return (
        <ExperimentVariant id={props.id} when={props.when} checkForBVariant={true}>
            {props.children}
        </ExperimentVariant>
    );
};
ExperimentVariantB.displayName = 'ExperimentVariantB';

const useExperiment = (expId: ExperimentId | ExperimentId[], variant: 'A' | 'B' = 'B') => {
    const experiments = useSelector(selectExperimentsFromBootstrap);
    return React.useMemo(() => {
        return isExperimentEnabled(experiments, expId, variant === 'B');
    }, [experiments, expId, variant]);
};

export const useExperimentIsA = (expId: ExperimentId | ExperimentId[]) => useExperiment(expId, 'A');
export const useExperimentIsB = (expId: ExperimentId | ExperimentId[]) => useExperiment(expId, 'B');

export default {
    WhenA: ExperimentVariantA,
    WhenB: ExperimentVariantB,
};
